/* eslint-disable max-len */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Container, Row, Card, CardBody, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/pro-regular-svg-icons/faFilePdf';
import { faFileAudio } from '@fortawesome/pro-regular-svg-icons/faFileAudio';
import classNames from 'classnames';
import ReactMarkdown from 'lib/react-markdown';
import Video from 'routes/Products/components/Video';
import ActionHourSectionSkeleton from './ActionHourSection.skeleton';
import { useActionHourData } from '../../hooks';
import { useTrack } from '../../../../products/WLC/contexts/TrackContext';
import { useWeek } from '../../../../products/WLC/contexts/WeekContext';

const ActionHourContentBase = ({ product, authToken, actionHoursOverride, weekNumberOverride }) => {
  const [bioModalOpen, setBioModalOpen] = useState(false);
  const { actionHours: currentTrackActionHours, getWeekNumber } = useTrack();
  const { week } = useWeek();

  // When previewing another track, use that track's action hours/week number
  // instead of the ones for the member's actual current track
  const actionHours = actionHoursOverride || currentTrackActionHours;
  const weekNumber = weekNumberOverride ?? (week?.slug ? getWeekNumber(week.slug) : 0);

  // Determine which action hour to use based on week number
  const selectedActionHour = useMemo(() => {
    if (!actionHours || actionHours.length === 0) return null;

    // Weeks 1-4: use first action hour (index 0)
    // Weeks 5-8: use second action hour (index 1)
    // Fallback: use first action hour if only 1 exists
    if (weekNumber >= 5 && actionHours.length > 1) {
      return actionHours[1];
    }
    return actionHours[0];
  }, [actionHours, weekNumber]);

  const {
    hasData,
    videoData,
    videoDescription,
    downloads,
    bioData,
    bioDescription,
    bioTitle,
    bioImage,
    loading,
    error
  } = useActionHourData(selectedActionHour?.slug, product, authToken);

  const toggleBioModal = () => setBioModalOpen(!bioModalOpen);

  if (loading) {
    return <ActionHourSectionSkeleton />;
  }

  if (error) {
    return <div>Error loading Action Hour</div>;
  }

  if (!hasData) {
    return null;
  }

  return (
    <>
      <h2 className="mb-4 mt-0">Your Monthly Action Hour</h2>
      <Video save {...videoData} />
      <ReactMarkdown>{videoDescription}</ReactMarkdown>
      <Button color="success" size="lg" block onClick={toggleBioModal}>
        Read more about {bioTitle}
      </Button>

      <Row className="justify-content-center mb-4">
        {downloads && downloads.length > 0 && (
          <Col xs={12}>
            <h5 className="text-center mt-2">Downloads</h5>
            <div className="d-flex justify-content-center flex-wrap text-center">
              {downloads.map((download, index) => {
                const downloadType = download?.data?.type;
                const downloadUrl = download?.data?.url;
                const downloadText = download?.data?.text;

                const getIcon = () => {
                  if (downloadType === 'MP3') {
                    return faFileAudio;
                  }
                  return faFilePdf;
                };

                return (
                  <a
                    href={downloadUrl}
                    download
                    key={download.id || index}
                    className="text-decoration-none d-flex flex-column align-items-center mx-3 mb-3 text-purple"
                  >
                    <FontAwesomeIcon icon={getIcon()} size="2x" className="mb-2" />
                    <span className="font-weight-bold">{downloadText || 'Download'}</span>
                  </a>
                );
              })}
            </div>
          </Col>
        )}
        <Col xs={12}>
          <Card className={classNames('background-light-gray', 'border-2')}>
            <CardBody>
              <p>
                <strong>Tip:</strong>
              </p>
              <p>This ~60-minute Action Hour is for Weeks {weekNumber > 4 ? '5–8' : '1–4'}.</p>
              <p>
                Don't feel like you have to watch it right away. Watching during Week{' '}
                {weekNumber > 4 ? '6 or 7 (or even 8!)' : '2 or 3 (or even 4!)'} might work better with your
                schedule.
              </p>
              {weekNumber <= 4 && <p className="mb-0">At the start of Week 5, you'll get a new one.</p>}
            </CardBody>
          </Card>
        </Col>
      </Row>
      {/* Bio Modal */}
      <Modal isOpen={bioModalOpen} toggle={toggleBioModal} size="lg" centered>
        <ModalHeader toggle={toggleBioModal}>{bioTitle}</ModalHeader>
        <ModalBody>
          {bioImage && (
            <div className="text-center mb-4">
              <img src={bioImage} alt={bioTitle} style={{ maxWidth: '200px', borderRadius: '50%' }} />
            </div>
          )}
          {bioDescription && <ReactMarkdown>{bioDescription}</ReactMarkdown>}
        </ModalBody>
      </Modal>
    </>
  );
};

export const ActionHourSection = props => (
  <Container className="pt-4">
    <ActionHourContentBase {...props} />
  </Container>
);
export const ActionHourCard = props => (
  <Card>
    <CardBody>
      <ActionHourContentBase {...props} />
    </CardBody>
  </Card>
);

ActionHourSection.propTypes = {
  product: PropTypes.string.isRequired,
  authToken: PropTypes.string.isRequired,
  actionHoursOverride: PropTypes.arrayOf(PropTypes.shape({})),
  weekNumberOverride: PropTypes.number
};

export default ActionHourSection;
